/**
 * AdminFlagsPanel.jsx
 * ═══════════════════════════════════════════════════════════════════════════════
 * Admin modal for editing feature flags in real-time.
 * 
 * FEATURES:
 * - Kensho-themed UI with teal/cyan gradient
 * - Only visible for admin users
 * - Animated toggle switches for all flags
 * - Nested flags (integrations, sharing, spark store)
 * - Real-time save with success animation
 * - Access via admin icon in chat panel header
 * 
 * USAGE:
 * - Import in chat-panel.jsx
 * - Show modal when admin icon clicked
 * - Auto-hides for non-admin users
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect } from 'react';
import { useFeatureFlags } from './FeatureFlags';
import UsageLogService from "../services/UsageLogService";
import ApiClient from "../services/ApiClient";
import useIsAdmin from "../hooks/useIsAdmin";

const AdminFlagsPanel = ({ isOpen, onClose, userEmail }) => {
    const currentFlags = useFeatureFlags();
    const [flags, setFlags] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState(''); // 'success' | 'error' | ''
    const [hasChanges, setHasChanges] = useState(false);

    const isAdmin = useIsAdmin();

    // Load flags when panel opens
    useEffect(() => {
        if (isOpen && isAdmin) {
            loadFlags();
        }
    }, [isOpen]);

    // Track page view for analytics when panel opens
    useEffect(() => {
        if (isOpen && isAdmin) {
            UsageLogService.trackPageView('AdminFlagsPanel', {
                userEmail: userEmail,
                hasChanges: hasChanges,
                flagsLoaded: !!flags
            });
        }
    }, [isOpen]);

    const loadFlags = async () => {
        setLoading(true);
        setSaveStatus('');
        try {
            console.log('[AdminFlags] 🔄 Starting flag load...');
            console.log('[AdminFlags] User email:', userEmail);

            console.log('[AdminFlags] 📡 Fetching from /api/admin/flags...');
            const data = await ApiClient.get('/api/admin/flags', {
                headers: { 'X-User-Email': userEmail }
            });
            console.log('[AdminFlags] ✅ Data received:', data);
            console.log('[AdminFlags] Has features?', data?.features != null);

            setFlags(data);
            setLoading(false);
            console.log('[AdminFlags] ✅ Flags loaded successfully!');
        } catch (error) {
            console.error('[AdminFlags] ❌ Load failed:', error);
            console.error('[AdminFlags] Error type:', error.constructor.name);
            console.error('[AdminFlags] Error message:', error.message);
            setLoading(false);
            setSaveStatus('error');
        }
    };

    const saveFlags = async () => {
        setSaving(true);
        setSaveStatus('');
        try {
            console.log('[AdminFlags] 💾 Starting save...');
            console.log('[AdminFlags] Flags to save:', JSON.stringify(flags, null, 2));

            console.log('[AdminFlags] 📡 POSTing to /api/admin/flags...');
            const result = await ApiClient.post('/api/admin/flags', flags, {
                headers: { 'X-User-Email': userEmail }
            });
            console.log('[AdminFlags] ✅ Save result:', result);

            setSaveStatus('success');
            setHasChanges(false);

            // Auto-hide success message after 3 seconds
            setTimeout(() => setSaveStatus(''), 3000);

            setSaving(false);
            console.log('[AdminFlags] ✅ Save completed successfully!');
        } catch (error) {
            console.error('[AdminFlags] ❌ Save failed:', error);
            console.error('[AdminFlags] Error type:', error.constructor.name);
            console.error('[AdminFlags] Error message:', error.message);
            console.error('[AdminFlags] Error stack:', error.stack);
            setSaveStatus('error');
            setSaving(false);
        }
    };

    const toggleFlag = (path) => {
        const parts = path.split('.');
        const newFlags = JSON.parse(JSON.stringify(flags)); // Deep clone

        let obj = newFlags.features;
        for (let i = 0; i < parts.length - 1; i++) {
            const key = parts[i];
            if (!obj[key] || typeof obj[key] !== 'object') {
                obj[key] = {};
            }
            obj = obj[key];
        }

        const lastKey = parts[parts.length - 1];
        if (obj[lastKey]?.enabled !== undefined) {
            obj[lastKey].enabled = !obj[lastKey].enabled;
        } else if (typeof obj[lastKey] === 'boolean') {
            obj[lastKey] = !obj[lastKey];
        } else if (!obj[lastKey]) {
            obj[lastKey] = { enabled: true };
        } else {
            obj[lastKey].enabled = !(obj[lastKey].enabled ?? false);
        }

        setFlags(newFlags);
        setHasChanges(true);
        setSaveStatus('');
    };

    if (!isOpen) return null;
    if (!isAdmin) return null;

    const isDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(8px)',
            zIndex: 999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'fadeIn 0.3s ease',
            padding: 20
        }}>
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slideUp {
                    from { transform: translateY(30px); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
                @keyframes successPulse {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                }
            `}</style>

            <div style={{
                background: isDark ? '#1e293b' : 'white',
                borderRadius: 24,
                width: '100%',
                maxWidth: 900,
                maxHeight: '90vh',
                overflow: 'hidden',
                boxShadow: '0 25px 80px rgba(0, 0, 0, 0.3)',
                animation: 'slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                display: 'flex',
                flexDirection: 'column'
            }}>
                {/* Header */}
                <div style={{
                    background: 'linear-gradient(135deg, #0d7e8f 0%, #14b8a6 50%, #06b6d4 100%)',
                    padding: '30px 40px',
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)',
                        pointerEvents: 'none'
                    }} />
                    
                    <div style={{ position: 'relative', zIndex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
                                {/* Kensho K logo */}
                                <div style={{
                                    width: 48,
                                    height: 48,
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    borderRadius: 12,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backdropFilter: 'blur(10px)'
                                }}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M6 3v18M6 12l12-9M6 12l12 9"/>
                                    </svg>
                                </div>
                                <div>
                                    <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.5px' }}>
                                        Feature Flags Admin
                                    </div>
                                    <div style={{ fontSize: 13, opacity: 0.9, fontWeight: 500 }}>
                                        Spark Anywhere · Real-time Configuration
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 9,
                                    border: 'none',
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    color: 'white',
                                    cursor: 'pointer',
                                    fontSize: 20,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
                                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
                            >×</button>
                        </div>
                        <div style={{ fontSize: 12, opacity: 0.85 }}>
                            👤 {userEmail}
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div style={{
                    flex: 1,
                    overflow: 'auto',
                    padding: 30
                }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: 60 }}>
                            <div style={{
                                width: 48,
                                height: 48,
                                border: '4px solid rgba(20, 184, 166, 0.2)',
                                borderTopColor: '#14b8a6',
                                borderRadius: '50%',
                                animation: 'spin 0.8s linear infinite',
                                margin: '0 auto 20px'
                            }} />
                            <div style={{ fontSize: 14, color: isDark ? '#94a3b8' : '#64748b' }}>
                                Loading flags...
                            </div>
                        </div>
                    ) : flags?.features ? (
                        <>
                            {renderSection('Core Features', [
                                { key: 'chatHistory', icon: '💬', name: 'Chat History', description: 'Show chat history sidebar' },
                                { key: 'agents', icon: '🤖', name: 'Agents', description: 'Show agents selection chip' },
                                { key: 'contentGeneration', icon: '📄', name: 'Content Generation', description: 'Word & PowerPoint agents' },
                                { key: 'createSpark', icon: '✨', name: 'Create Spark', description: 'Show Create Spark button' },
                                { key: 'voice', icon: '🎤', name: 'Voice Input', description: 'Microphone for speech-to-text' },
                                { key: 'kenshoGoogleSwitch', icon: '🔄', name: 'STT Provider', description: 'Google/Kensho toggle' },
                                { key: 'analyticsDashboard', icon: '📊', name: 'Analytics Dashboard', description: 'Show EWI Platform Analytics icon in header' }
                            ], flags, isDark, toggleFlag)}

                            {renderNestedSection('Spark Store', 'sparkStore', '🎪', [
                                { key: 'features.embedIcon', label: 'Embed Icon', icon: '<>' }
                            ], flags, isDark, toggleFlag)}

                            {renderNestedSection('Workplace', 'workplaceIntegrations', '🏢', [
                                { key: 'integrations.teams', label: 'Teams', icon: '📢' },
                                { key: 'integrations.outlook', label: 'Outlook', icon: '📧' },
                                { key: 'integrations.sharepoint', label: 'SharePoint', icon: '📁' },
                                { key: 'integrations.onedrive', label: 'OneDrive', icon: '☁️' },
                                { key: 'integrations.ado', label: 'Azure DevOps', icon: '📊' },
                                { key: 'integrations.calendar', label: 'Calendar', icon: '📅' }
                            ], flags, isDark, toggleFlag)}

                            {renderNestedSection('Workplace Agent Framework', 'workplaceAgentFramework', '🧠', [
                                { key: 'agents.teams', label: 'Teams Agent', icon: '📢' },
                                { key: 'agents.outlook', label: 'Outlook Agent', icon: '📧' },
                                { key: 'agents.onedrive', label: 'OneDrive Agent', icon: '☁️' },
                                { key: 'agents.calendar', label: 'Calendar Agent', icon: '📅' }
                            ], flags, isDark, toggleFlag)}

                            {renderNestedSection('Sharing', 'sharing', '📤', [
                                { key: 'options.email', label: 'Email', icon: '✉️' },
                                { key: 'options.teams', label: 'Teams', icon: '📢' },
                                { key: 'options.onedrive', label: 'OneDrive', icon: '☁️' },
                                { key: 'options.ado', label: 'Azure DevOps', icon: '📊' }
                            ], flags, isDark, toggleFlag)}
                        </>
                    ) : (
                        <div style={{ textAlign: 'center', padding: 40, color: '#ef4444' }}>
                            ❌ Failed to load flags
                        </div>
                    )}
                </div>

                {/* Footer / Save Bar */}
                {flags?.features && (
                    <div style={{
                        padding: '20px 40px',
                        borderTop: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
                        background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(248, 250, 252, 0.9)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 20
                    }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>
                            {saveStatus === 'success' ? (
                                <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span>✓</span> Changes saved successfully!
                                </span>
                            ) : saveStatus === 'error' ? (
                                <span style={{ color: '#ef4444', display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span>❌</span> Save failed - please try again
                                </span>
                            ) : hasChanges ? (
                                <span style={{ color: '#f59e0b', display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span>⚠️</span> You have unsaved changes
                                </span>
                            ) : null}
                        </div>
                        <button
                            onClick={saveFlags}
                            disabled={!hasChanges || saving}
                            style={{
                                padding: '12px 32px',
                                borderRadius: 12,
                                border: 'none',
                                background: saveStatus === 'success' 
                                    ? 'linear-gradient(135deg, #10b981, #059669)'
                                    : 'linear-gradient(135deg, #14b8a6, #06b6d4)',
                                color: 'white',
                                fontSize: 15,
                                fontWeight: 700,
                                cursor: hasChanges && !saving ? 'pointer' : 'not-allowed',
                                opacity: hasChanges && !saving ? 1 : 0.5,
                                boxShadow: hasChanges ? '0 8px 25px rgba(20, 184, 166, 0.35)' : 'none',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                animation: saveStatus === 'success' ? 'successPulse 0.6s ease' : 'none',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8
                            }}
                            onMouseEnter={e => {
                                if (hasChanges && !saving) {
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.boxShadow = '0 12px 35px rgba(20, 184, 166, 0.45)';
                                }
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = hasChanges ? '0 8px 25px rgba(20, 184, 166, 0.35)' : 'none';
                            }}
                        >
                            {saving ? (
                                <>
                                    <div style={{
                                        width: 16,
                                        height: 16,
                                        border: '2px solid rgba(255, 255, 255, 0.3)',
                                        borderTopColor: 'white',
                                        borderRadius: '50%',
                                        animation: 'spin 0.6s linear infinite'
                                    }} />
                                    Saving...
                                </>
                            ) : saveStatus === 'success' ? (
                                <>✓ Saved!</>
                            ) : (
                                <>💾 Save Changes</>
                            )}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );

    // ── Render Helpers ──────────────────────────────────────────────────────────
    function renderSection(title, flagsList, flags, isDark, toggleFlag) {
        return (
            <div style={{ marginBottom: 30 }}>
                <div style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: isDark ? '#f1f5f9' : '#1e293b',
                    marginBottom: 16,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                }}>
                    <div style={{
                        width: 3,
                        height: 20,
                        background: 'linear-gradient(135deg, #14b8a6, #06b6d4)',
                        borderRadius: 999
                    }} />
                    {title}
                </div>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                    gap: 16
                }}>
                    {flagsList.map(f => renderFlagCard(f, flags.features, isDark, toggleFlag))}
                </div>
            </div>
        );
    }

    function renderFlagCard(f, features, isDark, toggleFlag) {
        const enabled = features[f.key]?.enabled ?? false;
        
        return (
            <div key={f.key} style={{
                background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'white',
                border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
                borderRadius: 16,
                padding: 20,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
                position: 'relative'
            }}
            onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = isDark 
                    ? '0 8px 30px rgba(20, 184, 166, 0.2)'
                    : '0 8px 30px rgba(20, 184, 166, 0.15)';
                e.currentTarget.style.borderColor = 'rgba(20, 184, 166, 0.4)';
            }}
            onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = isDark ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0';
            }}
            onClick={() => toggleFlag(f.key)}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 22 }}>{f.icon}</span>
                        <span style={{
                            fontSize: 15,
                            fontWeight: 700,
                            color: isDark ? '#f1f5f9' : '#1e293b'
                        }}>{f.name}</span>
                    </div>
                    <div style={{
                        padding: '3px 8px',
                        borderRadius: 999,
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        background: enabled ? 'rgba(16, 185, 129, 0.15)' : 'rgba(148, 163, 184, 0.15)',
                        color: enabled ? '#059669' : '#64748b'
                    }}>
                        {enabled ? '✓ ON' : '○ OFF'}
                    </div>
                </div>
                <div style={{
                    fontSize: 12,
                    color: isDark ? '#94a3b8' : '#64748b',
                    marginBottom: 14,
                    lineHeight: 1.5
                }}>{f.description}</div>
                <div style={{
                    width: 52,
                    height: 28,
                    borderRadius: 999,
                    background: enabled 
                        ? 'linear-gradient(135deg, #14b8a6, #06b6d4)'
                        : isDark ? 'rgba(255, 255, 255, 0.15)' : '#cbd5e1',
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    boxShadow: enabled ? '0 4px 15px rgba(20, 184, 166, 0.4)' : 'none'
                }}>
                    <div style={{
                        position: 'absolute',
                        top: 3,
                        left: enabled ? 26 : 3,
                        width: 22,
                        height: 22,
                        background: 'white',
                        borderRadius: '50%',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                    }} />
                </div>
            </div>
        );
    }

    function renderNestedSection(title, parentKey, icon, children, flags, isDark, toggleFlag) {
        const parentValue = flags.features[parentKey];
        const parentEnabled = typeof parentValue === 'boolean'
            ? parentValue
            : (parentValue?.enabled ?? false);
        
        return (
            <div style={{ marginBottom: 30 }}>
                <div style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: isDark ? '#f1f5f9' : '#1e293b',
                    marginBottom: 16,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                }}>
                    <div style={{
                        width: 3,
                        height: 20,
                        background: 'linear-gradient(135deg, #14b8a6, #06b6d4)',
                        borderRadius: 999
                    }} />
                    {icon} {title}
                </div>

                {/* Parent toggle */}
                <div style={{
                    background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'white',
                    border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
                    borderRadius: 16,
                    padding: 20,
                    marginBottom: 16,
                    cursor: 'pointer',
                    transition: 'all 0.3s'
                }}
                onClick={() => toggleFlag(parentKey)}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{
                            fontSize: 15,
                            fontWeight: 700,
                            color: isDark ? '#f1f5f9' : '#1e293b'
                        }}>
                            Enable {title}
                        </div>
                        <div style={{
                            width: 52,
                            height: 28,
                            borderRadius: 999,
                            background: parentEnabled 
                                ? 'linear-gradient(135deg, #14b8a6, #06b6d4)'
                                : isDark ? 'rgba(255, 255, 255, 0.15)' : '#cbd5e1',
                            position: 'relative',
                            transition: 'all 0.3s ease'
                        }}>
                            <div style={{
                                position: 'absolute',
                                top: 3,
                                left: parentEnabled ? 26 : 3,
                                width: 22,
                                height: 22,
                                background: 'white',
                                borderRadius: '50%',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                            }} />
                        </div>
                    </div>
                </div>

                {/* Child toggles */}
                {parentEnabled && (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                        gap: 12
                    }}>
                        {children.map(child => {
                            const childPath = `${parentKey}.${child.key}`;
                            const childValue = getNestedValue(flags.features, childPath);
                            const childEnabled = typeof childValue === 'boolean'
                                ? childValue
                                : (childValue?.enabled ?? false);
                            
                            return (
                                <div key={childPath} style={{
                                    background: isDark ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc',
                                    border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
                                    borderRadius: 12,
                                    padding: 14,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                                onClick={(e) => { e.stopPropagation(); toggleFlag(childPath); }}
                                onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255, 255, 255, 0.08)' : '#f1f5f9'}
                                onMouseLeave={e => e.currentTarget.style.background = isDark ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc'}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: isDark ? '#cbd5e1' : '#475569'
                                    }}>
                                        <span style={{ fontSize: 16 }}>{child.icon}</span>
                                        {child.label}
                                    </div>
                                    <div style={{
                                        width: 40,
                                        height: 22,
                                        borderRadius: 999,
                                        background: childEnabled 
                                            ? 'linear-gradient(135deg, #14b8a6, #06b6d4)'
                                            : isDark ? 'rgba(255, 255, 255, 0.15)' : '#cbd5e1',
                                        position: 'relative',
                                        transition: 'all 0.3s ease'
                                    }}>
                                        <div style={{
                                            position: 'absolute',
                                            top: 2,
                                            left: childEnabled ? 20 : 2,
                                            width: 18,
                                            height: 18,
                                            background: 'white',
                                            borderRadius: '50%',
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)'
                                        }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }

    function getNestedValue(obj, path) {
        const parts = path.split('.');
        let current = obj;
        for (const part of parts) {
            if (!current) return null;
            current = current[part];
        }
        return current;
    }
};

export default AdminFlagsPanel;